import {
  OC_HEAD_COMPARISON_FIELDS,
  OC_HEAD_DELIVERY_ADDRESS_FIELDS,
  toFlagFieldId,
  type OcHeadComparisonFieldId,
} from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import type { OrderConfirmationHead } from '@/types/orderConfirmation'
import type { PurchaseOrderHead } from '@/types/purchaseOrder'
import type { OrderConfirmationSettings } from '@/types/orderConfirmationSettings'

const ADDRESS_NORMALIZED_COMPARISON_FIELDS = new Set<OcHeadComparisonFieldId>([
  'invoice_address_company_name',
  'invoice_address_street',
  'invoice_address_postal_code',
  'invoice_address_city',
  'delivery_address_company_name',
  'delivery_address_street',
  'delivery_address_postal_code',
  'delivery_address_city',
])

export function normHeadValue(v: string | undefined): string {
  return (v ?? '').trim()
}

/** Normalize address fields for comparison: ignore case, spaces, and dashes. */
export function normAddressComparisonValue(v: string | undefined): string {
  return (v ?? '').replace(/[\s-]+/g, '').toLowerCase()
}

function normValueForComparison(
  v: string | undefined,
  fieldId?: OcHeadComparisonFieldId,
): string {
  if (fieldId && ADDRESS_NORMALIZED_COMPARISON_FIELDS.has(fieldId)) {
    return normAddressComparisonValue(v)
  }
  return normHeadValue(v)
}

export function headValuesDiffer(
  oc: string | undefined,
  po: string | undefined,
  fieldId?: OcHeadComparisonFieldId,
): boolean {
  const o = normValueForComparison(oc, fieldId)
  const p = normValueForComparison(po, fieldId)
  return Boolean(o && p && o !== p)
}

function headFieldValue(
  head: OrderConfirmationHead | PurchaseOrderHead,
  fieldId: OcHeadComparisonFieldId,
): string | undefined {
  return String(head[fieldId as keyof typeof head] ?? '')
}

export function isHeadComparisonEnabled(
  settings: OrderConfirmationSettings | undefined,
  fieldId: OcHeadComparisonFieldId,
): boolean {
  if (!settings) return false
  return settings[toFlagFieldId(fieldId)]
}

/**
 * Payment terms are compared on what both sides were parsed to, the way the backend check compares them, so
 * "30 dagar netto" against 30 is not a deviation. Without a parsed value on the order confirmation — no term was found
 * in its text, or the API predates the field — the text is compared as before.
 */
function paymentTermsDiffer(ocHead: OrderConfirmationHead, poHead: PurchaseOrderHead): boolean {
  const ocTerm = ocHead.parsed_payment_terms
  if (ocTerm == null) {
    return headValuesDiffer(ocHead.payment_terms, poHead.payment_terms, 'payment_terms')
  }
  const poTerm = poHead.parsed_payment_terms
  return poTerm != null && ocTerm !== poTerm
}

export function headFieldShouldHighlight(
  ocHead: OrderConfirmationHead,
  poHead: PurchaseOrderHead | undefined,
  settings: OrderConfirmationSettings | undefined,
  fieldId: OcHeadComparisonFieldId,
): boolean {
  if (!poHead || !isHeadComparisonEnabled(settings, fieldId)) return false
  if (fieldId === 'payment_terms') return paymentTermsDiffer(ocHead, poHead)
  return headValuesDiffer(headFieldValue(ocHead, fieldId), headFieldValue(poHead, fieldId), fieldId)
}

export function deliveryAddressShouldHighlight(
  ocHead: OrderConfirmationHead,
  poHead: PurchaseOrderHead | undefined,
  settings: OrderConfirmationSettings | undefined,
): boolean {
  if (!poHead || !settings) return false
  return OC_HEAD_DELIVERY_ADDRESS_FIELDS.some((field) =>
    headFieldShouldHighlight(ocHead, poHead, settings, field.id),
  )
}

export function countConfiguredHeadDifferences(
  ocHead: OrderConfirmationHead | undefined,
  poHead: PurchaseOrderHead | undefined,
  settings: OrderConfirmationSettings | undefined,
): number {
  if (!ocHead || !poHead || !settings) return 0
  return OC_HEAD_COMPARISON_FIELDS.filter((field) =>
    headFieldShouldHighlight(ocHead, poHead, settings, field.id),
  ).length
}

export interface OcHeadSummaryDeviations {
  deliveryTerms: boolean
  paymentTerms: boolean
  address: boolean
  sideNote: boolean
}

export function computeOcHeadSummaryDeviations(
  ocHead: OrderConfirmationHead,
  poHead: PurchaseOrderHead | undefined,
  settings: OrderConfirmationSettings | undefined,
  highlight: boolean,
): OcHeadSummaryDeviations {
  if (!highlight || !poHead || !settings) {
    return { deliveryTerms: false, paymentTerms: false, address: false, sideNote: false }
  }
  return {
    deliveryTerms: headFieldShouldHighlight(ocHead, poHead, settings, 'delivery_terms'),
    paymentTerms: headFieldShouldHighlight(ocHead, poHead, settings, 'payment_terms'),
    address: deliveryAddressShouldHighlight(ocHead, poHead, settings),
    sideNote: headFieldShouldHighlight(ocHead, poHead, settings, 'supplier_contact_person'),
  }
}
